import React, { useState, useEffect } from 'react';
import { getDoctorConfig } from '../config/doctorConfigs';

const DoctorAdminDashboard = ({ doctorSlug }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authCredentials, setAuthCredentials] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedAuth = localStorage.getItem(`doctor_auth_${doctorSlug}`);
    if (savedAuth) {
      try {
        const credentials = JSON.parse(savedAuth);
        fetch(`/api/doctor/simple-referrals?doctorId=${doctorSlug}&limit=1`, {
          headers: { 'Authorization': `Basic ${btoa(`${credentials.username}:${credentials.password}`)}` }
        }).then(response => {
          if (response.ok) {
            setAuthCredentials(credentials);
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem(`doctor_auth_${doctorSlug}`);
          }
          setIsLoading(false);
        }).catch(() => {
          localStorage.removeItem(`doctor_auth_${doctorSlug}`);
          setIsLoading(false);
        });
      } catch (e) {
        localStorage.removeItem(`doctor_auth_${doctorSlug}`);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [doctorSlug]);

  const handleLogin = async (username, password) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/doctor/simple-referrals?doctorId=${doctorSlug}`, {
        headers: { 'Authorization': `Basic ${btoa(`${username}:${password}`)}` }
      });
      if (response.ok) {
        const credentials = { username, password };
        setAuthCredentials(credentials);
        setIsAuthenticated(true);
        localStorage.setItem(`doctor_auth_${doctorSlug}`, JSON.stringify(credentials));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid credentials');
      }
    } catch (error) {
      alert('Login failed: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthCredentials(null);
    localStorage.removeItem(`doctor_auth_${doctorSlug}`);
  };

  const handlePasswordChanged = () => {
    setIsAuthenticated(false);
    setAuthCredentials(null);
    localStorage.removeItem(`doctor_auth_${doctorSlug}`);
  };

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} doctorSlug={doctorSlug} isLoading={isLoading} />;
  }

  return <Dashboard doctorSlug={doctorSlug} auth={authCredentials} onLogout={handleLogout} onPasswordChanged={handlePasswordChanged} />;
};

const LoginScreen = ({ onLogin, doctorSlug, isLoading }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (username && password) onLogin(username, password);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="mb-8">
            <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
            <p className="text-gray-500 mt-1">Sign in to access your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                placeholder="Enter your username"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                placeholder="Enter your password"
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !username || !password}
              className="w-full bg-teal-500 text-white py-3 px-4 rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">{doctorSlug}</p>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ doctorSlug, auth, onLogout, onPasswordChanged }) => {
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterType, setFilterType] = useState('preset'); // 'preset' or 'custom'
  const [presetFilter, setPresetFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, limit: null, usage: 0 });
  const [severityFilter, setSeverityFilter] = useState('');
  const [concernFilter, setConcernFilter] = useState('');
  const [expandedPatientId, setExpandedPatientId] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [useNewUi, setUseNewUi] = useState(() => {
    try {
      return localStorage.getItem(`doctor_admin_ui_v2_${doctorSlug}`) === 'true';
    } catch {
      return false;
    }
  });
  const doctorConfig = getDoctorConfig(doctorSlug);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [debouncedSearch, presetFilter, filterType, severityFilter, concernFilter]);

  useEffect(() => {
    if (selectedPatient && !patients.some(patient => patient.referralId === selectedPatient.referralId)) {
      setSelectedPatient(null);
    }
  }, [patients, selectedPatient]);

  const toggleNewUi = () => {
    setUseNewUi(prev => {
      const next = !prev;
      try {
        localStorage.setItem(`doctor_admin_ui_v2_${doctorSlug}`, String(next));
      } catch {}
      return next;
    });
  };

  const fetchData = async (customStart, customEnd) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ doctorId: doctorSlug, limit: '500' });

      if (filterType === 'custom' && (customStart || startDate) && (customEnd || endDate)) {
        params.append('startDate', customStart || startDate);
        params.append('endDate', customEnd || endDate);
      } else {
        params.append('months', presetFilter);
      }

      if (debouncedSearch) params.append('search', debouncedSearch);
      if (severityFilter) params.append('severity', severityFilter);
      if (concernFilter) params.append('concern', concernFilter);

      const response = await fetch(`/api/doctor/simple-referrals?${params}`, {
        headers: { 'Authorization': `Basic ${btoa(`${auth.username}:${auth.password}`)}` }
      });

      if (!response.ok) throw new Error('Failed to load data');

      const data = await response.json();
      setPatients(data.directReferrals || []);
      setStats({
        total: data.totalReportsEver || 0,
        thisMonth: data.currentMonthUsage || 0,
        limit: data.monthlyLimit,
        usage: data.currentMonthUsage || 0
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const applyCustomDates = () => {
    if (startDate && endDate) {
      fetchData(startDate, endDate);
    }
  };

  const downloadCSV = async () => {
    try {
      // Fetch all patients for export
      const response = await fetch(`/api/doctor/simple-referrals?doctorId=${doctorSlug}&months=all&limit=10000`, {
        headers: { 'Authorization': `Basic ${btoa(`${auth.username}:${auth.password}`)}` }
      });
      if (response.ok) {
        const data = await response.json();
        const allPatients = data.directReferrals || [];

        // Build CSV with new columns
        const escapeCSV = (val) => {
          if (val === null || val === undefined) return '';
          const str = String(val);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        };

        const headers = ['Referral ID', 'Patient Name', 'Phone', 'Age', 'City', 'State', 'Primary Concern', 'Visit Purpose', 'Severity', 'Date'];
        const rows = allPatients.map(p => [
          escapeCSV(p.referralId),
          escapeCSV(p.patientName),
          escapeCSV(p.patientPhone),
          escapeCSV(p.patientAge),
          escapeCSV(p.patientCity),
          escapeCSV(p.patientState),
          escapeCSV(p.primaryConcern),
          escapeCSV(p.visitPurpose),
          escapeCSV(p.analysisResult?.overallSeverity),
          escapeCSV(new Date(p.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }))
        ].join(','));

        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `patients-${doctorSlug}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      alert('Download failed');
    }
  };

  const deletePatient = async (id, name) => {
    if (!confirm(`Remove ${name} from your list?`)) return;
    try {
      const response = await fetch(`/api/doctor/delete-patient?doctorId=${doctorSlug}&patientId=${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${btoa(`${auth.username}:${auth.password}`)}` }
      });
      if (response.ok) fetchData();
      else throw new Error('Failed to remove');
    } catch (err) {
      alert('Failed to remove patient');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => fetchData()} className="text-teal-600 hover:underline">Try again</button>
        </div>
      </div>
    );
  }

  const usagePercent = stats.limit ? Math.min(100, (stats.usage / stats.limit) * 100) : 0;

  if (useNewUi) {
    return (
      <NewDoctorAdminDashboard
        doctorSlug={doctorSlug}
        doctorConfig={doctorConfig}
        patients={patients}
        stats={stats}
        usagePercent={usagePercent}
        isLoading={isLoading}
        search={search}
        setSearch={setSearch}
        filterType={filterType}
        setFilterType={setFilterType}
        presetFilter={presetFilter}
        setPresetFilter={setPresetFilter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        severityFilter={severityFilter}
        setSeverityFilter={setSeverityFilter}
        concernFilter={concernFilter}
        setConcernFilter={setConcernFilter}
        applyCustomDates={applyCustomDates}
        downloadCSV={downloadCSV}
        deletePatient={deletePatient}
        fetchData={fetchData}
        selectedPatient={selectedPatient}
        setSelectedPatient={setSelectedPatient}
        onLogout={onLogout}
        onToggleUi={toggleNewUi}
        onChangePassword={() => setShowPasswordModal(true)}
        showPasswordModal={showPasswordModal}
        auth={auth}
        onClosePasswordModal={() => setShowPasswordModal(false)}
        onPasswordChanged={onPasswordChanged}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Patient Analytics</h1>
                <p className="text-sm text-gray-500">{doctorSlug}</p>
              </div>
              <button
                onClick={toggleNewUi}
                className="ml-3 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
              >
                Try out our new UI
              </button>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => fetchData()} className="text-gray-400 hover:text-gray-600 transition-colors" title="Refresh">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button onClick={() => setShowPasswordModal(true)} className="text-sm text-gray-500 hover:text-gray-700 font-medium">Change Password</button>
              <button onClick={onLogout} className="text-sm text-gray-500 hover:text-gray-700 font-medium">Sign Out</button>
            </div>
          </div>
        </div>
      </header>

      {showPasswordModal && (
        <ChangePasswordModal
          doctorSlug={doctorSlug}
          auth={auth}
          onClose={() => setShowPasswordModal(false)}
          onPasswordChanged={onPasswordChanged}
        />
      )}

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Patients</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">This Month</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.thisMonth}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-gray-500">Monthly Usage</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.usage}<span className="text-lg text-gray-400 font-normal">/{stats.limit || '∞'}</span></p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            {stats.limit && (
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-5 border-b border-gray-100">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or phone..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Filter Type Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setFilterType('preset')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filterType === 'preset' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                >
                  Quick Filter
                </button>
                <button
                  onClick={() => setFilterType('custom')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filterType === 'custom' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                >
                  Custom Dates
                </button>
              </div>

              {/* Export Button */}
              <button
                onClick={downloadCSV}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export CSV
              </button>
            </div>
          </div>

          {/* Filter Options */}
          <div className="p-5">
            {filterType === 'preset' ? (
              <div className="flex flex-wrap gap-2">
                {[
                  { value: '1', label: 'Last Month' },
                  { value: '3', label: 'Last 3 Months' },
                  { value: '6', label: 'Last 6 Months' },
                  { value: '12', label: 'Last Year' },
                  { value: 'all', label: 'All Time' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setPresetFilter(opt.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      presetFilter === opt.value
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <button
                  onClick={applyCustomDates}
                  disabled={!startDate || !endDate}
                  className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          {/* Severity & Concern Filters */}
          <div className="px-5 pb-5 pt-0 border-t border-gray-100">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 pt-4">
              {/* Severity Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Severity:</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setSeverityFilter('')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      severityFilter === '' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setSeverityFilter('Low')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      severityFilter === 'Low' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'
                    }`}
                  >
                    Low
                  </button>
                  <button
                    onClick={() => setSeverityFilter('Moderate')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      severityFilter === 'Moderate' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    Moderate
                  </button>
                  <button
                    onClick={() => setSeverityFilter('High')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      severityFilter === 'High' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    High
                  </button>
                </div>
              </div>

              {/* Concern Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Concern:</span>
                <select
                  value={concernFilter}
                  onChange={(e) => setConcernFilter(e.target.value)}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-white"
                >
                  <option value="">All Concerns</option>
                  <option value="acne">Acne</option>
                  <option value="pigmentation">Pigmentation</option>
                  <option value="wrinkles">Wrinkles</option>
                  <option value="dark_circles">Dark Circles</option>
                  <option value="dull_skin">Dull Skin</option>
                  <option value="open_pores">Open Pores</option>
                  <option value="acne_scar">Acne Scars</option>
                  <option value="uneven_skin_tone">Uneven Skin Tone</option>
                  <option value="loose_skin">Loose Skin</option>
                  <option value="normal_skin">General Checkup</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Patients Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Patient List</h2>
            <span className="text-sm text-gray-500">{isLoading ? 'Loading...' : `${patients.length} records`}</span>
          </div>

          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block w-8 h-8 border-2 border-gray-200 border-t-teal-500 rounded-full animate-spin"></div>
              <p className="text-gray-500 mt-3">Loading patients...</p>
            </div>
          ) : patients.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium">No patients found</p>
              <p className="text-gray-400 text-sm mt-1">{debouncedSearch ? 'Try a different search term' : 'Patients will appear after they use your analysis page'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Patient</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Concern</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Severity</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {patients.map((p) => {
                    const isExpanded = expandedPatientId === p.referralId;
                    const severity = p.analysisResult?.overallSeverity || 'Unknown';
                    const severityColors = {
                      Low: 'bg-green-100 text-green-700',
                      Moderate: 'bg-amber-100 text-amber-700',
                      High: 'bg-red-100 text-red-700',
                      Unknown: 'bg-gray-100 text-gray-500'
                    };

                    return (
                      <React.Fragment key={p.referralId}>
                        <tr
                          onClick={() => setExpandedPatientId(isExpanded ? null : p.referralId)}
                          className={`hover:bg-gray-50 transition-colors cursor-pointer ${isExpanded ? 'bg-teal-50' : ''}`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                {p.patientName?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <span className="font-medium text-gray-900">{p.patientName}</span>
                                {p.patientAge && (
                                  <span className="text-gray-500 text-sm ml-1">({p.patientAge})</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-gray-600 font-mono text-sm">{p.patientPhone}</span>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            {p.primaryConcern ? (
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                                {p.primaryConcern.replace(/_/g, ' ')}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${severityColors[severity] || severityColors.Unknown}`}>
                              {severity}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-gray-500 text-sm">
                              {new Date(p.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <svg
                                className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                              <button
                                onClick={(e) => { e.stopPropagation(); deletePatient(p.referralId, p.patientName); }}
                                className="text-gray-400 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50"
                                title="Remove patient"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="6" className="px-6 py-4 bg-gray-50">
                              <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
                                {/* Patient's Self-Report */}
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Patient's Self-Report</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <p className="text-xs text-gray-500 mb-1">Primary Concern</p>
                                      <p className="text-sm font-medium text-gray-900 capitalize">{p.primaryConcern?.replace(/_/g, ' ') || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <p className="text-xs text-gray-500 mb-1">Visit Purpose</p>
                                      <p className="text-sm font-medium text-gray-900">{p.visitPurpose || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <p className="text-xs text-gray-500 mb-1">Condition Duration</p>
                                      <p className="text-sm font-medium text-gray-900">{p.conditionDuration || '-'}</p>
                                    </div>
                                  </div>
                                </div>

                                {/* AI Analysis Results */}
                                {p.analysisResult ? (
                                  <div>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">AI Analysis Results</h4>

                                    {/* Overall Assessment */}
                                    {p.analysisResult.overallCondition && (
                                      <p className="text-sm text-gray-700 mb-4">{p.analysisResult.overallCondition}</p>
                                    )}

                                    {/* Metrics Row */}
                                    <div className="flex flex-wrap gap-3 mb-4">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500">Severity:</span>
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${severityColors[p.analysisResult.overallSeverity] || severityColors.Unknown}`}>
                                          {p.analysisResult.overallSeverity || 'Unknown'}
                                        </span>
                                      </div>
                                      {p.analysisResult.skinType && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-500">Skin Type:</span>
                                          <span className="text-sm font-medium text-gray-900">{p.analysisResult.skinType}</span>
                                        </div>
                                      )}
                                      {p.analysisResult.skinAge && (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-500">Skin Age:</span>
                                          <span className="text-sm font-medium text-gray-900">{p.analysisResult.skinAge}</span>
                                        </div>
                                      )}
                                    </div>

                                    {/* AI-Detected Concerns */}
                                    {p.analysisResult.primaryConcerns?.length > 0 && (
                                      <div className="mb-4">
                                        <p className="text-xs text-gray-500 mb-2">AI-Detected Concerns</p>
                                        <div className="flex flex-wrap gap-2">
                                          {p.analysisResult.primaryConcerns.map((concern, idx) => (
                                            <span key={idx} className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                              {concern}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Detailed Analysis */}
                                    {p.analysisResult.detailedAnalysis && Object.keys(p.analysisResult.detailedAnalysis).length > 0 && (
                                      <div className="mb-4">
                                        <p className="text-xs text-gray-500 mb-2">Detailed Analysis</p>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                          {Object.entries(p.analysisResult.detailedAnalysis)
                                            .slice(0, 6)
                                            .map(([condition, value]) => {
                                              // value can be object {percentage, severity, description} or just a number
                                              const numValue = typeof value === 'object' ? (value?.percentage || 0) : (typeof value === 'number' ? value : parseInt(value) || 0);
                                              const barColor = numValue < 30 ? 'bg-green-500' : numValue < 60 ? 'bg-amber-500' : 'bg-red-500';
                                              return (
                                                <div key={condition} className="bg-gray-50 rounded-lg p-3">
                                                  <div className="flex justify-between items-center mb-1">
                                                    <span className="text-xs font-medium text-gray-700 capitalize">{condition.replace(/_/g, ' ')}</span>
                                                    <span className="text-xs text-gray-500">{numValue}%</span>
                                                  </div>
                                                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className={`h-full ${barColor} rounded-full`} style={{ width: `${numValue}%` }} />
                                                  </div>
                                                </div>
                                              );
                                            })}
                                        </div>
                                      </div>
                                    )}

                                    {/* Summary */}
                                    {p.analysisResult.summary && (
                                      <div className="mb-4">
                                        <p className="text-xs text-gray-500 mb-1">Summary</p>
                                        <p className="text-sm text-gray-700">{p.analysisResult.summary}</p>
                                      </div>
                                    )}

                                    {/* Recommendations */}
                                    {p.analysisResult.recommendations && (
                                      <div>
                                        <p className="text-xs text-gray-500 mb-2">Recommendations</p>
                                        <div className="space-y-2">
                                          {p.analysisResult.recommendations.skincare && (
                                            <div>
                                              <p className="text-xs font-medium text-gray-600 mb-1">Skincare</p>
                                              <p className="text-sm text-gray-700">{typeof p.analysisResult.recommendations.skincare === 'string' ? p.analysisResult.recommendations.skincare : p.analysisResult.recommendations.skincare.join(', ')}</p>
                                            </div>
                                          )}
                                          {p.analysisResult.recommendations.followup && (
                                            <div>
                                              <p className="text-xs font-medium text-gray-600 mb-1">Follow-up</p>
                                              <p className="text-sm text-gray-700">{typeof p.analysisResult.recommendations.followup === 'string' ? p.analysisResult.recommendations.followup : p.analysisResult.recommendations.followup.join(', ')}</p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-center py-4">
                                    <p className="text-sm text-gray-500">No analysis data available</p>
                                  </div>
                                )}

                                {/* Location */}
                                {(p.patientCity || p.patientState) && (
                                  <div className="pt-4 border-t border-gray-100">
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                      <span>{[p.patientCity, p.patientState].filter(Boolean).join(', ')}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const toTitleLabel = (value) => String(value || '-').replace(/_/g, ' ');

const getSeverityClass = (severity) => {
  const severityKey = severity || 'Unknown';
  const classes = {
    Low: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    Moderate: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    High: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
    Unknown: 'bg-stone-100 text-stone-500 ring-1 ring-stone-200'
  };
  return classes[severityKey] || classes.Unknown;
};

const getPatientMonthKey = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthLabel = (date) => date.toLocaleDateString('en-IN', { month: 'short' });

const buildPatientTrend = (patients) => {
  const validDates = patients
    .map(patient => patient.createdAt ? new Date(patient.createdAt) : null)
    .filter(date => date && !Number.isNaN(date.getTime()));
  const anchor = validDates.length ? new Date(Math.max(...validDates.map(date => date.getTime()))) : new Date();
  const monthCounts = patients.reduce((acc, patient) => {
    const key = getPatientMonthKey(patient.createdAt);
    if (key) acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(anchor.getFullYear(), anchor.getMonth() - (5 - index), 1);
    const key = getPatientMonthKey(date);
    return {
      key,
      label: getMonthLabel(date),
      count: monthCounts[key] || 0
    };
  });
};

const buildConcernMix = (patients) => {
  const counts = patients.reduce((acc, patient) => {
    const label = toTitleLabel(patient.primaryConcern || 'Unknown');
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);
};

const normalizeSeverity = (severity) => {
  const value = String(severity || 'Unknown').toLowerCase();
  if (value.includes('high') || value.includes('severe')) return 'High';
  if (value.includes('moderate') || value.includes('medium')) return 'Moderate';
  if (value.includes('low') || value.includes('mild')) return 'Low';
  return 'Unknown';
};

const buildSeveritySplit = (patients) => {
  const counts = { Low: 0, Moderate: 0, High: 0, Unknown: 0 };
  patients.forEach(patient => {
    counts[normalizeSeverity(patient.analysisResult?.overallSeverity)] += 1;
  });

  return ['Low', 'Moderate', 'High', 'Unknown']
    .map(label => ({ label, count: counts[label] }))
    .filter(item => item.count > 0 || item.label !== 'Unknown');
};

const NewDoctorAdminDashboard = ({
  doctorSlug,
  doctorConfig,
  patients,
  stats,
  usagePercent,
  isLoading,
  search,
  setSearch,
  filterType,
  setFilterType,
  presetFilter,
  setPresetFilter,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  severityFilter,
  setSeverityFilter,
  concernFilter,
  setConcernFilter,
  applyCustomDates,
  downloadCSV,
  deletePatient,
  fetchData,
  selectedPatient,
  setSelectedPatient,
  onLogout,
  onToggleUi,
  onChangePassword,
  showPasswordModal,
  auth,
  onClosePasswordModal,
  onPasswordChanged
}) => {
  const brand = doctorConfig || {
    name: doctorSlug,
    clinic: doctorSlug,
    logoUrl: null,
    logoAlt: doctorSlug,
    accentColor: '#0f766e',
    accentColorDark: '#115e59'
  };
  const accent = brand.accentColor || '#0f766e';
  const accentDark = brand.accentColorDark || accent;
  const trendData = buildPatientTrend(patients);
  const concernMix = buildConcernMix(patients);
  const severitySplit = buildSeveritySplit(patients);
  const maxTrend = Math.max(1, ...trendData.map(item => item.count));
  const maxConcern = Math.max(1, ...concernMix.map(item => item.count));
  const maxSeverity = Math.max(1, ...severitySplit.map(item => item.count));

  const statCards = [
    { label: 'Total Patients', value: stats.total || 0, detail: 'All reports' },
    { label: 'This Month', value: stats.thisMonth || 0, detail: 'Current usage' },
    { label: 'Monthly Usage', value: stats.limit ? `${stats.usage || 0}/${stats.limit}` : `${stats.usage || 0}/unlimited`, detail: `${Math.round(usagePercent || 0)}% used` }
  ];

  return (
    <div data-testid="doctor-admin-v2" className="min-h-screen bg-[#f5f1ea] text-[#211f1b]">
      <div className="flex min-h-screen">
        <aside className="hidden lg:flex w-72 shrink-0 flex-col bg-[#211f1b] text-white">
          <div className="px-7 py-8 border-b border-white/10">
            <div className="inline-flex min-h-16 min-w-24 items-center justify-center rounded-md bg-white px-4 py-3">
              {brand.logoUrl ? (
                <img
                  src={brand.logoUrl}
                  alt={brand.logoAlt || brand.clinic || doctorSlug}
                  className="max-h-12 max-w-36 object-contain"
                  onError={(event) => { event.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <span className="text-sm font-bold text-[#211f1b]">{brand.initials || 'DR'}</span>
              )}
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.28em] text-white/45">Doctor workspace</p>
            <p className="mt-2 text-base font-semibold text-white/85">{brand.name || doctorSlug}</p>
          </div>

          <div className="flex-1 px-7 py-7 space-y-3">
            <button
              onClick={onToggleUi}
              className="w-full rounded-lg border border-white/15 bg-white/8 px-4 py-3 text-left text-sm font-semibold text-white hover:bg-white/14 transition-colors"
            >
              Use classic UI
            </button>
            <button
              onClick={() => fetchData()}
              className="w-full rounded-lg px-4 py-3 text-left text-sm font-medium text-white/70 hover:bg-white/8 hover:text-white transition-colors"
            >
              Refresh Data
            </button>
          </div>

          <div className="space-y-2 px-7 py-6 border-t border-white/10">
            <p className="mb-3 text-xs uppercase tracking-[0.22em] text-white/35">Account</p>
            <button
              onClick={onChangePassword}
              className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-white/70 hover:bg-white/8 hover:text-white transition-colors"
            >
              Change Password
            </button>
            <button onClick={onLogout} className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-white/60 hover:bg-white/8 hover:text-white transition-colors">
              Sign Out
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <header className="bg-[#2b2823] px-4 py-5 text-white shadow-sm sm:px-8 lg:px-10">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex items-center gap-4">
                <div className="lg:hidden inline-flex min-h-12 min-w-20 items-center justify-center rounded-md bg-white px-3 py-2">
                  {brand.logoUrl ? (
                    <img
                      src={brand.logoUrl}
                      alt={brand.logoAlt || brand.clinic || doctorSlug}
                      className="max-h-9 max-w-28 object-contain"
                      onError={(event) => { event.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-xs font-bold text-[#211f1b]">{brand.initials || 'DR'}</span>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-white/45">Patient operations</p>
                  <h1 className="mt-1 text-2xl font-semibold tracking-tight">Worklist overview</h1>
                  <p className="text-sm text-white/58">{patients.length} visible records</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={onToggleUi}
                  className="lg:hidden rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-white/80 hover:bg-white/10"
                >
                  Use classic UI
                </button>
                <button
                  onClick={downloadCSV}
                  className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors"
                  style={{ backgroundColor: accent }}
                >
                  Export CSV
                </button>
              </div>
            </div>
          </header>

          <section className="px-4 py-6 sm:px-8 lg:px-10">
            <div className="grid gap-4 md:grid-cols-3">
              {statCards.map(card => (
                <div key={card.label} className="rounded-xl border border-[#e4ddd1] bg-[#fffdf9] p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8a8175]">{card.label}</p>
                  <p className="mt-3 text-3xl font-semibold tracking-tight text-[#211f1b]">{card.value}</p>
                  <p className="mt-1 text-sm text-[#8a8175]">{card.detail}</p>
                  {card.label === 'Monthly Usage' && stats.limit && (
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#ebe3d7]">
                      <div className="h-full rounded-full" style={{ width: `${usagePercent}%`, backgroundColor: accent }} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.85fr)]">
              <section data-testid="patient-trend-chart" className="rounded-xl border border-[#e4ddd1] bg-[#fffdf9] p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8a8175]">Patient trend</p>
                    <h2 className="mt-2 text-lg font-semibold text-[#211f1b]">Monthly reports</h2>
                  </div>
                  <span className="rounded-full bg-[#f1ede5] px-3 py-1 text-xs font-semibold text-[#574f46]">Last 6 months</span>
                </div>
                <div className="mt-6 flex h-40 items-end gap-3 border-b border-[#e4ddd1] pb-3">
                  {trendData.map(item => (
                    <div key={item.key} className="flex h-full flex-1 flex-col justify-end">
                      <div
                        className="min-h-2 rounded-t-md transition-all"
                        style={{ height: `${Math.max(8, (item.count / maxTrend) * 100)}%`, backgroundColor: item.count ? accent : '#e7dfd2' }}
                        title={`${item.label}: ${item.count} reports`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-6 gap-3">
                  {trendData.map(item => (
                    <div key={`${item.key}-label`} className="text-center">
                      <p className="text-xs font-semibold text-[#574f46]">{item.label}</p>
                      <p className="text-xs text-[#8a8175]">{item.count}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section data-testid="severity-chart" className="rounded-xl border border-[#e4ddd1] bg-[#fffdf9] p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8a8175]">Severity split</p>
                <h2 className="mt-2 text-lg font-semibold text-[#211f1b]">Clinical priority</h2>
                <div className="mt-6 space-y-4">
                  {severitySplit.map(item => (
                    <div key={item.label}>
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="font-semibold text-[#574f46]">{item.label}</span>
                        <span className="text-[#8a8175]">{item.count}</span>
                      </div>
                      <div className="h-2.5 overflow-hidden rounded-full bg-[#eee7db]">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${Math.max(8, (item.count / maxSeverity) * 100)}%`, backgroundColor: item.label === 'High' ? '#be123c' : item.label === 'Moderate' ? '#d97706' : accent }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <section data-testid="concern-mix-chart" className="mt-4 rounded-xl border border-[#e4ddd1] bg-[#fffdf9] p-5 shadow-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8a8175]">Concern mix</p>
                  <h2 className="mt-2 text-lg font-semibold text-[#211f1b]">Most common patient concerns</h2>
                </div>
                <p className="text-sm text-[#8a8175]">Based on the current filtered records</p>
              </div>
              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {(concernMix.length ? concernMix : [{ label: 'No concerns yet', count: 0 }]).map(item => (
                  <div key={item.label} className="rounded-lg bg-[#f8f3ea] p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold capitalize text-[#211f1b]">{item.label}</span>
                      <span className="text-[#8a8175]">{item.count}</span>
                    </div>
                    <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[#e4ddd1]">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${item.count ? Math.max(8, (item.count / maxConcern) * 100) : 0}%`, backgroundColor: accentDark }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-6 rounded-xl border border-[#e4ddd1] bg-[#fffdf9] p-4 shadow-sm">
              <div className="grid gap-3 xl:grid-cols-[minmax(260px,1fr)_auto_auto_auto_auto] xl:items-center">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#9a9185]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search by patient name or phone"
                    className="w-full rounded-lg border border-[#ded5c8] bg-white py-3 pl-10 pr-4 text-sm text-[#211f1b] outline-none transition focus:border-[#211f1b]"
                  />
                </div>

                <select
                  value={presetFilter}
                  onChange={(event) => { setFilterType('preset'); setPresetFilter(event.target.value); }}
                  className="rounded-lg border border-[#ded5c8] bg-white px-3 py-3 text-sm outline-none focus:border-[#211f1b]"
                >
                  <option value="all">All Time</option>
                  <option value="1">Last Month</option>
                  <option value="3">Last 3 Months</option>
                  <option value="6">Last 6 Months</option>
                  <option value="12">Last Year</option>
                </select>

                <select
                  value={severityFilter}
                  onChange={(event) => setSeverityFilter(event.target.value)}
                  className="rounded-lg border border-[#ded5c8] bg-white px-3 py-3 text-sm outline-none focus:border-[#211f1b]"
                >
                  <option value="">All Severity</option>
                  <option value="Low">Low</option>
                  <option value="Moderate">Moderate</option>
                  <option value="High">High</option>
                </select>

                <select
                  value={concernFilter}
                  onChange={(event) => setConcernFilter(event.target.value)}
                  className="rounded-lg border border-[#ded5c8] bg-white px-3 py-3 text-sm outline-none focus:border-[#211f1b]"
                >
                  <option value="">All Concerns</option>
                  <option value="acne">Acne</option>
                  <option value="pigmentation">Pigmentation</option>
                  <option value="wrinkles">Wrinkles</option>
                  <option value="dark_circles">Dark Circles</option>
                  <option value="open_pores">Open Pores</option>
                </select>

                <button
                  onClick={() => setFilterType(filterType === 'custom' ? 'preset' : 'custom')}
                  className="rounded-lg border border-[#ded5c8] px-3 py-3 text-sm font-semibold text-[#574f46] hover:bg-[#f5f1ea]"
                >
                  {filterType === 'custom' ? 'Quick Dates' : 'Custom Dates'}
                </button>
              </div>

              {filterType === 'custom' && (
                <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className="rounded-lg border border-[#ded5c8] bg-white px-3 py-3 text-sm outline-none focus:border-[#211f1b]"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className="rounded-lg border border-[#ded5c8] bg-white px-3 py-3 text-sm outline-none focus:border-[#211f1b]"
                  />
                  <button
                    onClick={applyCustomDates}
                    className="rounded-lg px-4 py-3 text-sm font-semibold text-white"
                    style={{ backgroundColor: accentDark }}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col gap-5 transition-all duration-300 xl:flex-row">
              <div className="min-w-0 flex-1 rounded-xl border border-[#e4ddd1] bg-[#fffdf9] shadow-sm">
                <div className="flex items-center justify-between border-b border-[#eee7db] px-5 py-4">
                  <div>
                    <h3 className="text-base font-semibold text-[#211f1b]">Patients</h3>
                    <p className="text-sm text-[#8a8175]">{isLoading ? 'Loading records' : `${patients.length} records shown`}</p>
                  </div>
                </div>

                {isLoading ? (
                  <div className="p-12 text-center">
                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-[#d8cfc0] border-t-[#211f1b]" />
                    <p className="mt-3 text-sm text-[#8a8175]">Loading patients...</p>
                  </div>
                ) : patients.length === 0 ? (
                  <div className="p-12 text-center">
                    <p className="font-medium text-[#211f1b]">No patients found</p>
                    <p className="mt-1 text-sm text-[#8a8175]">Patients will appear after they use your analysis page.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#eee7db]">
                    {patients.map(patient => {
                      const isSelected = selectedPatient?.referralId === patient.referralId;
                      return (
                        <div
                          key={patient.referralId}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedPatient(patient)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedPatient(patient);
                            }
                          }}
                          className={`grid cursor-pointer gap-4 px-5 py-4 transition-colors md:grid-cols-[minmax(180px,1.3fr)_minmax(130px,0.8fr)_minmax(110px,0.7fr)_minmax(100px,0.6fr)_auto] md:items-center ${isSelected ? 'bg-[#f3ece1]' : 'hover:bg-[#faf6ee]'}`}
                        >
                          <div className="min-w-0">
                            <button
                              type="button"
                              onClick={(event) => { event.stopPropagation(); setSelectedPatient(patient); }}
                              className="block truncate text-left text-sm font-semibold text-[#211f1b] hover:underline"
                            >
                              {patient.patientName || 'Unknown Patient'}
                            </button>
                            <p className="mt-1 text-xs text-[#8a8175]">{patient.patientAge ? `${patient.patientAge} yrs` : 'Age not provided'}</p>
                          </div>
                          <p className="font-mono text-sm text-[#574f46]">{patient.patientPhone || '-'}</p>
                          <span className="inline-flex w-fit rounded-full bg-[#f1ede5] px-2.5 py-1 text-xs font-semibold capitalize text-[#574f46]">
                            {toTitleLabel(patient.primaryConcern)}
                          </span>
                          <span className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${getSeverityClass(patient.analysisResult?.overallSeverity)}`}>
                            {patient.analysisResult?.overallSeverity || 'Unknown'}
                          </span>
                          <div className="flex items-center justify-between gap-3 md:justify-end">
                            <span className="text-xs text-[#8a8175]">{formatDate(patient.createdAt)}</span>
                            <button
                              type="button"
                              onClick={(event) => { event.stopPropagation(); deletePatient(patient.referralId, patient.patientName); }}
                              className="rounded-md px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div
                className={`overflow-hidden transition-all duration-300 ${selectedPatient ? 'opacity-100 translate-x-0' : 'h-0 pointer-events-none opacity-0 translate-x-6 xl:h-auto'}`}
                style={{ width: selectedPatient ? '24rem' : 0, maxWidth: '100%', flexShrink: 0 }}
              >
                {selectedPatient && (
                  <PatientDetailPanel patient={selectedPatient} accent={accent} onClose={() => setSelectedPatient(null)} />
                )}
              </div>
            </div>
          </section>
        </main>
      </div>

      {showPasswordModal && (
        <ChangePasswordModal
          doctorSlug={doctorSlug}
          auth={auth}
          onClose={onClosePasswordModal}
          onPasswordChanged={onPasswordChanged}
        />
      )}
    </div>
  );
};

const PatientDetailPanel = ({ patient, accent, onClose }) => {
  const analysis = patient.analysisResult || {};
  const detailEntries = Object.entries(analysis.detailedAnalysis || {}).slice(0, 6);

  return (
    <aside data-testid="patient-detail-panel" className="sticky top-5 rounded-xl border border-[#e4ddd1] bg-[#fffdf9] shadow-xl shadow-stone-900/8">
      <div className="flex items-start justify-between border-b border-[#eee7db] px-5 py-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8a8175]">Patient detail</p>
          <h3 className="mt-2 text-xl font-semibold text-[#211f1b]">{patient.patientName || 'Unknown Patient'}</h3>
          <p className="mt-1 font-mono text-sm text-[#574f46]">{patient.patientPhone || '-'}</p>
        </div>
        <button onClick={onClose} className="rounded-md px-2 py-1 text-sm font-semibold text-[#8a8175] hover:bg-[#f5f1ea]">
          Close
        </button>
      </div>

      <div className="max-h-[calc(100vh-150px)] overflow-y-auto px-5 py-5 space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <DetailTile label="Age" value={patient.patientAge || '-'} />
          <DetailTile label="Date" value={formatDate(patient.createdAt)} />
          <DetailTile label="Concern" value={toTitleLabel(patient.primaryConcern)} />
          <DetailTile label="Visit" value={patient.visitPurpose || '-'} />
        </div>

        {(patient.patientCity || patient.patientState) && (
          <div className="rounded-lg bg-[#f8f3ea] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8a8175]">Location</p>
            <p className="mt-1 text-sm font-medium text-[#211f1b]">{[patient.patientCity, patient.patientState].filter(Boolean).join(', ')}</p>
          </div>
        )}

        <div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getSeverityClass(analysis.overallSeverity)}`}>
              {analysis.overallSeverity || 'Unknown'}
            </span>
            {analysis.skinType && <span className="text-sm text-[#8a8175]">{analysis.skinType} skin</span>}
          </div>
          {analysis.overallCondition && (
            <p className="mt-3 text-sm leading-6 text-[#574f46]">{analysis.overallCondition}</p>
          )}
        </div>

        {analysis.primaryConcerns?.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8a8175]">AI-detected concerns</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {analysis.primaryConcerns.map((concern) => (
                <span key={concern} className="rounded-full bg-[#f1ede5] px-3 py-1 text-xs font-semibold text-[#574f46]">
                  {concern}
                </span>
              ))}
            </div>
          </div>
        )}

        {detailEntries.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8a8175]">Detailed analysis</p>
            <div className="mt-3 space-y-3">
              {detailEntries.map(([condition, value]) => {
                const amount = typeof value === 'object' ? (value?.percentage || 0) : (typeof value === 'number' ? value : parseInt(value, 10) || 0);
                return (
                  <div key={condition}>
                    <div className="flex justify-between text-xs">
                      <span className="font-semibold capitalize text-[#574f46]">{toTitleLabel(condition)}</span>
                      <span className="text-[#8a8175]">{amount}%</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-[#eee7db]">
                      <div className="h-full rounded-full" style={{ width: `${amount}%`, backgroundColor: accent }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(analysis.summary || analysis.recommendations) && (
          <div className="rounded-lg border border-[#eee7db] bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8a8175]">Clinical notes</p>
            {analysis.summary && <p className="mt-2 text-sm leading-6 text-[#574f46]">{analysis.summary}</p>}
            {analysis.recommendations?.skincare && (
              <p className="mt-3 text-sm leading-6 text-[#574f46]">
                <span className="font-semibold text-[#211f1b]">Skincare: </span>
                {Array.isArray(analysis.recommendations.skincare) ? analysis.recommendations.skincare.join(', ') : analysis.recommendations.skincare}
              </p>
            )}
            {analysis.recommendations?.followup && (
              <p className="mt-2 text-sm leading-6 text-[#574f46]">
                <span className="font-semibold text-[#211f1b]">Follow-up: </span>
                {Array.isArray(analysis.recommendations.followup) ? analysis.recommendations.followup.join(', ') : analysis.recommendations.followup}
              </p>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

const DetailTile = ({ label, value }) => (
  <div className="rounded-lg bg-[#f8f3ea] p-3">
    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8a8175]">{label}</p>
    <p className="mt-1 text-sm font-semibold capitalize text-[#211f1b]">{value}</p>
  </div>
);

const ChangePasswordModal = ({ doctorSlug, auth, onClose, onPasswordChanged }) => {
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.newPassword !== form.confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }

    if (form.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch('/api/doctor/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${auth.username}:${form.currentPassword}`)}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          doctorId: doctorSlug,
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
          confirmPassword: form.confirmPassword
        })
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to change password');
      }

      onPasswordChanged();
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
          <p className="text-sm text-gray-500 mt-1">You will need to sign in again after this change.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-100 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
            <input
              type="password"
              value={form.currentPassword}
              onChange={(e) => updateField('currentPassword', e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              autoComplete="current-password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
            <input
              type="password"
              value={form.newPassword}
              onChange={(e) => updateField('newPassword', e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
            <input
              type="password"
              value={form.confirmPassword}
              onChange={(e) => updateField('confirmPassword', e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-teal-500 text-white py-3 px-4 rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
            >
              {isSubmitting ? 'Changing...' : 'Change Password'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-100 text-gray-600 py-3 px-4 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-semibold transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DoctorAdminDashboard;
